/**
 * Theme Service
 * API client for theme CRUD management across all scopes
 */

import api from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api.constants'
import { branchSettingsConstants } from '@/config/api/branch/branch-settings.constants'
import { getCachedData, setCachedData, getCacheKey } from '@/utils/localStorageCache.util'

export interface Theme {
  id: string
  key: string
  name: string
  description: string | null
  mode: 'light' | 'dark' | 'system'
  config: {
    light?: Record<string, string>
    dark?: Record<string, string>
    [key: string]: any
  }
  isDefault: boolean
  isActive: boolean
  isSystem: boolean
  createdAt: string
  updatedAt: string
}

export interface ThemeListResponse {
  themes: Theme[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
    hasNext: boolean
    hasPrev: boolean
  }
}

export interface ThemeStats {
  totalThemes: number
  activeThemes: number
  inactiveThemes: number
  systemThemes: number
  defaultThemes: number
  themesByMode: Array<{
    mode: string
    count: number
  }>
}

export interface GetThemesParams {
  page?: number
  limit?: number
  search?: string
  isActive?: boolean
  mode?: 'light' | 'dark' | 'system'
}

export interface CreateThemeInput {
  key: string
  name: string
  description?: string | null
  mode: 'light' | 'dark' | 'system'
  config: {
    light?: Record<string, string>
    dark?: Record<string, string>
    [key: string]: any
  }
  isDefault?: boolean
  isActive?: boolean
}

export interface UpdateThemeInput {
  name?: string
  description?: string | null
  mode?: 'light' | 'dark' | 'system'
  config?: {
    light?: Record<string, string>
    dark?: Record<string, string>
    [key: string]: any
  }
  isDefault?: boolean
  isActive?: boolean
}

class ThemeService {
  /**
   * Get all themes with pagination and filters (Platform scope)
   */
  async getThemes(params: GetThemesParams = {}): Promise<ThemeListResponse> {
    try {
      const response = await api.get(API_ENDPOINTS.PLATFORM.THEMES.LIST, {
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          isActive: params.isActive,
          mode: params.mode,
        },
      })
      
      const apiData = response.data
      // Backend returns: { success: true, data: { themes: [...], pagination: {...} } }
      // So apiData.data contains { themes: [...], pagination: {...} }
      const result = apiData?.data || {}
      const themes = Array.isArray(result.themes) ? result.themes : (Array.isArray(result) ? result : [])
      
      return {
        themes,
        pagination: result.pagination || apiData?.pagination || {
          page: 1,
          limit: 10,
          total: themes.length,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        },
      }
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Get themes for organization scope
   */
  async getOrgThemes(orgSlug: string, params: GetThemesParams = {}): Promise<ThemeListResponse> {
    try {
      const response = await api.get(API_ENDPOINTS.ORG.THEMES.LIST(orgSlug), {
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          isActive: params.isActive,
          mode: params.mode,
        },
      })
      
      const apiData = response.data
      return {
        themes: apiData?.data || [],
        pagination: apiData?.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        },
      }
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Get themes for branch scope
   */
  async getBranchThemes(orgSlug: string, branchSlug: string, params: GetThemesParams = {}): Promise<ThemeListResponse> {
    try {
      const response = await api.get(branchSettingsConstants.THEMES.LIST(orgSlug, branchSlug), {
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          isActive: params.isActive,
          mode: params.mode,
        },
      })
      
      const apiData = response.data
      return {
        themes: apiData?.data || [],
        pagination: apiData?.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        },
      }
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Get theme by ID (Platform scope)
   */
  async getThemeById(id: string): Promise<Theme> {
    try {
      const response = await api.get(API_ENDPOINTS.PLATFORM.THEMES.GET_BY_ID(id))
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Get theme by ID (Organization scope)
   */
  async getOrgThemeById(orgSlug: string, id: string): Promise<Theme> {
    try {
      const response = await api.get(API_ENDPOINTS.ORG.THEMES.GET_BY_ID(orgSlug, id))
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Get theme by ID (Branch scope)
   */
  async getBranchThemeById(orgSlug: string, branchSlug: string, id: string): Promise<Theme> {
    try {
      const response = await api.get(branchSettingsConstants.THEMES.GET_BY_ID(orgSlug, branchSlug, id))
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Create theme (Platform scope)
   */
  async createTheme(input: CreateThemeInput): Promise<Theme> {
    try {
      const response = await api.post(API_ENDPOINTS.PLATFORM.THEMES.CREATE, input)
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Create theme (Organization scope)
   */
  async createOrgTheme(orgSlug: string, input: CreateThemeInput): Promise<Theme> {
    try {
      const response = await api.post(API_ENDPOINTS.ORG.THEMES.CREATE(orgSlug), input)
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Create theme (Branch scope)
   */
  async createBranchTheme(orgSlug: string, branchSlug: string, input: CreateThemeInput): Promise<Theme> {
    try {
      const response = await api.post(branchSettingsConstants.THEMES.CREATE(orgSlug, branchSlug), input)
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Update theme (Platform scope)
   */
  async updateTheme(id: string, input: UpdateThemeInput): Promise<Theme> {
    try {
      const response = await api.patch(API_ENDPOINTS.PLATFORM.THEMES.UPDATE(id), input)
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Update theme (Organization scope)
   */
  async updateOrgTheme(orgSlug: string, id: string, input: UpdateThemeInput): Promise<Theme> {
    try {
      const response = await api.patch(API_ENDPOINTS.ORG.THEMES.UPDATE(orgSlug, id), input)
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Update theme (Branch scope)
   */
  async updateBranchTheme(orgSlug: string, branchSlug: string, id: string, input: UpdateThemeInput): Promise<Theme> {
    try {
      const response = await api.patch(branchSettingsConstants.THEMES.UPDATE(orgSlug, branchSlug, id), input)
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Delete theme (Platform scope)
   */
  async deleteTheme(id: string): Promise<void> {
    try {
      await api.delete(API_ENDPOINTS.PLATFORM.THEMES.DELETE(id))
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Delete theme (Organization scope)
   */
  async deleteOrgTheme(orgSlug: string, id: string): Promise<void> {
    try {
      await api.delete(API_ENDPOINTS.ORG.THEMES.DELETE(orgSlug, id))
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Delete theme (Branch scope)
   */
  async deleteBranchTheme(orgSlug: string, branchSlug: string, id: string): Promise<void> {
    try {
      await api.delete(branchSettingsConstants.THEMES.DELETE(orgSlug, branchSlug, id))
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Set theme as default (Platform scope)
   */
  async setAsDefault(id: string): Promise<Theme> {
    try {
      const response = await api.post(API_ENDPOINTS.PLATFORM.THEMES.SET_DEFAULT(id))
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Set theme as default (Organization scope)
   */
  async setOrgAsDefault(orgSlug: string, id: string): Promise<Theme> {
    try {
      const response = await api.post(API_ENDPOINTS.ORG.THEMES.SET_DEFAULT(orgSlug, id))
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Set theme as default (Branch scope)
   */
  async setBranchAsDefault(orgSlug: string, branchSlug: string, id: string): Promise<Theme> {
    try {
      const response = await api.post(branchSettingsConstants.THEMES.SET_DEFAULT(orgSlug, branchSlug, id))
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Get platform default theme for public shells (landing pages).
   * PUBLIC — no auth required. Always hits the network; React Query handles caching.
   */
  async getPublicTheme(): Promise<Theme | null> {
    try {
      const response = await api.get(API_ENDPOINTS.PLATFORM.THEMES.PUBLIC)
      return (response.data?.data ?? response.data) as Theme | null
    } catch {
      return null
    }
  }

  /**
   * Get current user's active theme (Platform scope)
   * Uses localStorage cache to avoid unnecessary API calls
   */
  async getCurrentTheme(forceRefresh = false): Promise<Theme | null> {
    const cacheKey = getCacheKey('currentTheme')
    
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = getCachedData<Theme>(cacheKey)
      if (cached !== null) {
        return cached
      }
    }

    try {
      const response = await api.get(API_ENDPOINTS.PLATFORM.THEMES.GET_CURRENT)
      const data = response.data.data
      
      // Cache the result (5 minutes TTL)
      if (data) {
        setCachedData(cacheKey, data, 5 * 60 * 1000)
      }
      
      return data
    } catch (error: any) {
      // Handle 404 gracefully (no theme set)
      if (error.response?.status === 404) {
        // Cache null result for shorter time (1 minute) to avoid repeated 404s
        setCachedData(cacheKey, null, 1 * 60 * 1000)
        return null
      }
      // Handle 401/403 gracefully (auth issues)
      if (error.response?.status === 401 || error.response?.status === 403) {
        return null
      }
      // Log other errors but don't throw - allow graceful fallback
      // Return null instead of throwing to allow graceful fallback
      return null
    }
  }

  /**
   * Get current user's active theme (Organization scope)
   */
  async getCurrentOrgTheme(orgSlug: string): Promise<Theme | null> {
    try {
      const response = await api.get(API_ENDPOINTS.ORG.THEMES.GET_CURRENT(orgSlug))
      return response.data.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  }

  /**
   * Get current user's active theme (Branch scope)
   */
  async getCurrentBranchTheme(orgSlug: string, branchSlug: string): Promise<Theme | null> {
    try {
      const response = await api.get(branchSettingsConstants.THEMES.GET_CURRENT(orgSlug, branchSlug))
      return response.data.data
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null
      }
      throw error
    }
  }

  /**
   * Get theme statistics (Platform scope)
   */
  async getThemeStats(): Promise<ThemeStats> {
    try {
      const response = await api.get(API_ENDPOINTS.PLATFORM.THEMES.STATS)
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Get theme statistics (Organization scope)
   */
  async getOrgThemeStats(orgSlug: string): Promise<ThemeStats> {
    try {
      const response = await api.get(API_ENDPOINTS.ORG.THEMES.STATS(orgSlug))
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }

  /**
   * Get theme statistics (Branch scope)
   */
  async getBranchThemeStats(orgSlug: string, branchSlug: string): Promise<ThemeStats> {
    try {
      const response = await api.get(branchSettingsConstants.THEMES.STATS(orgSlug, branchSlug))
      return response.data.data
    } catch (error: any) {
      throw error
    }
  }
}

export const themeService = new ThemeService()
export default themeService

